import { useState } from 'react'
import { Plus, X, Mail } from 'lucide-react'
import { Input, Label, Button, Badge } from '@/components/ui'
import { WizardStep, WizardActions } from './WizardStep'
import type { FamilyRole } from '@/types'

export interface InviteMember {
  email: string
  role: FamilyRole
}

interface InviteMembersStepProps {
  invites: InviteMember[]
  onInvitesChange: (invites: InviteMember[]) => void
  onNext: () => void
  onBack: () => void
  onSkip: () => void
  isLoading?: boolean
}

const ROLE_OPTIONS: { value: FamilyRole; label: string; description: string }[] = [
  {
    value: 'PARENT',
    label: 'Parent',
    description: 'Family member being monitored',
  },
  {
    value: 'CAREGIVER',
    label: 'Caregiver',
    description: 'Family member who monitors others',
  },
]

export function InviteMembersStep({
  invites,
  onInvitesChange,
  onNext,
  onBack,
  onSkip,
  isLoading = false,
}: InviteMembersStepProps) {
  const [newEmail, setNewEmail] = useState('')
  const [newRole, setNewRole] = useState<FamilyRole>('PARENT')
  const [emailError, setEmailError] = useState('')

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const handleAddInvite = () => {
    const email = newEmail.trim().toLowerCase()

    if (!email) {
      setEmailError('Please enter an email address')
      return
    }

    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address')
      return
    }

    if (invites.some((i) => i.email === email)) {
      setEmailError('This email has already been added')
      return
    }

    onInvitesChange([...invites, { email, role: newRole }])
    setNewEmail('')
    setEmailError('')
  }

  const handleRemoveInvite = (email: string) => {
    onInvitesChange(invites.filter((i) => i.email !== email))
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAddInvite()
    }
  }

  return (
    <WizardStep
      title="Who else should join?"
      description="Invite the people you want in your care circle"
    >
      <div className="space-y-4">
        {/* Add new invite form */}
        <div className="rounded-lg border border-gray-200 p-4">
          <div className="space-y-3">
            <div>
              <Label htmlFor="email">Email address</Label>
              <Input
                id="email"
                type="email"
                placeholder="family@example.com"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value)
                  setEmailError('')
                }}
                onKeyDown={handleKeyDown}
                className="mt-1"
              />
              {emailError && <p className="mt-1 text-xs text-error-600">{emailError}</p>}
            </div>

            <div>
              <Label>Role</Label>
              <div className="mt-1 flex gap-2">
                {ROLE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setNewRole(option.value)}
                    className={`flex-1 rounded-md border p-3 text-left transition-colors ${
                      newRole === option.value
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-sm font-medium text-gray-900">{option.label}</div>
                    <div className="text-xs text-gray-500">{option.description}</div>
                  </button>
                ))}
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={handleAddInvite}
              className="w-full"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add to invite list
            </Button>
          </div>
        </div>

        {/* Invite list */}
        {invites.length > 0 && (
          <div className="space-y-2">
            <Label>Pending invites ({invites.length})</Label>
            <div className="space-y-2">
              {invites.map((invite) => (
                <div
                  key={invite.email}
                  className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2"
                >
                  <div className="flex items-center gap-3">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <div>
                      <div className="text-sm font-medium text-gray-900">{invite.email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">
                      {invite.role === 'PARENT' ? 'Parent' : 'Caregiver'}
                    </Badge>
                    <button
                      type="button"
                      onClick={() => handleRemoveInvite(invite.email)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {invites.length === 0 && (
          <p className="text-center text-sm text-gray-500">
            No rush — you can always invite people later from the Family page.
          </p>
        )}
      </div>

      <WizardActions
        onBack={onBack}
        onNext={onNext}
        onSkip={onSkip}
        nextLabel={invites.length > 0 ? `Send ${invites.length} invite${invites.length > 1 ? 's' : ''} & Continue` : 'Finish Setup'}
        isNextLoading={isLoading}
        showSkip={invites.length > 0}
        skipLabel="Skip invites"
      />
    </WizardStep>
  )
}
